import { useState, useEffect, useRef, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/lib/supabase";
import { IMG_BASE } from "@/lib/images";
import { GALLERY_REGISTRY, getGalleryDef, galleryKeyForLang } from "@/lib/galleries";
import { formatAmount, toUsd } from "@/lib/currency";
import DatePicker from "@/components/base/DatePicker";
import { invokeAdmin } from "@/pages/backstage/lib/adminApi";
import BausteinBookings from "@/pages/backstage/components/BausteinBookings";
import TransferPrices from "@/pages/backstage/components/TransferPrices";
import TourBlockedDates from "@/pages/backstage/components/TourBlockedDates";

type Reservation = {
  id: string;
  booking_no: string | null;
  villa_slug: string;
  check_in: string;
  check_out: string;
  adults: number;
  children: number;
  firstname: string;
  lastname: string;
  email: string;
  phone: string | null;
  nationality: string | null;
  message: string | null;
  status: string;
  created_at: string;
};

type BlockedDate = {
  id: string;
  villa_slug: string;
  date: string;
  reason: string | null;
};

type BausteinPrice = {
  key: string;
  category: string;
  label_de: string;
  label_en: string;
  amount: number;
  amount_eur: number | null;
  unit: string;
  sort_order: number;
};

const PRICE_GROUPS = [
  { category: "villa", labelDe: "Villen (pro Nacht)", labelEn: "Villas (per night)" },
  { category: "excursion", labelDe: "Ausflüge (pro Person)", labelEn: "Excursions (per person)" },
  { category: "roundtrip", labelDe: "Rundreisen (pro Person)", labelEn: "Round trips (per person)" },
  { category: "experience", labelDe: "Erlebnisse (pro Person)", labelEn: "Experiences (per person)" },
  { category: "transfer", labelDe: "Transfers (pro Fahrt)", labelEn: "Transfers (per trip)" },
  { category: "wedding", labelDe: "Hochzeiten (Paketpreis)", labelEn: "Weddings (package price)" },
  { category: "wedding_addon", labelDe: "Hochzeit Zusatzleistungen", labelEn: "Wedding add-ons" },
  { category: "discount", labelDe: "Ermäßigung", labelEn: "Discount" },
];

const VILLAS = [
  { slug: "villa-frangipani", name: "Villa Frangipani" },
  { slug: "villa-bougainvillea", name: "Villa Bougainvillea" },
  { slug: "villa-hibiskus", name: "Villa Hibiskus" },
  { slug: "villa-gesamt", name: "Gesamtes Anwesen (alle 3 Villen)" },
];

const villaName = (slug: string) => VILLAS.find((v) => v.slug === slug)?.name ?? slug;

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

// Wandelt ein ISO-Datum ("YYYY-MM-DD") in eine vergleichbare Tagesnummer um,
// damit Zeiträume ohne Zeitzonen-Verschiebung gefiltert werden können.
const dayNum = (iso: string | null | undefined): number => {
  const parts = (iso || "").slice(0, 10).split("-").map(Number);
  if (parts.length < 3 || !parts[0] || !parts[1] || !parts[2]) return NaN;
  return Date.UTC(parts[0], parts[1] - 1, parts[2]);
};

const PHOTO_FOLDERS = [
  { value: "home", labelDe: "Startseite / Hero", labelEn: "Homepage / Hero" },
  { value: "about", labelDe: "Über uns", labelEn: "About" },
  { value: "contact", labelDe: "Kontakt / Header", labelEn: "Contact / header" },
  { value: "your-stay", labelDe: "Your Stay / Header", labelEn: "Your stay / header" },
  { value: "villas", labelDe: "Villen – Header", labelEn: "Villas – header" },
  { value: "villas/villa-gallery", labelDe: "Villen – Übersicht Galerie", labelEn: "Villas – overview gallery" },
  { value: "villas/villa-frangipani", labelDe: "Villa Frangipani", labelEn: "Villa Frangipani" },
  { value: "villas/villa-bougainvillea", labelDe: "Villa Bougainvillea", labelEn: "Villa Bougainvillea" },
  { value: "villas/villa-hibiscus", labelDe: "Villa Hibiskus", labelEn: "Villa Hibiscus" },
  { value: "activities/day-trips", labelDe: "Aktivitäten – Tagesausflüge", labelEn: "Activities – day trips" },
  { value: "activities/multi-day-tours", labelDe: "Aktivitäten – Mehrtagestouren", labelEn: "Activities – multi-day tours" },
  { value: "activities/adventure-tours", labelDe: "Aktivitäten – Adventure-Touren", labelEn: "Activities – adventure tours" },
  { value: "activities/tour-info", labelDe: "Aktivitäten – Tour-Infos", labelEn: "Activities – tour info" },
  { value: "activities/health-sports", labelDe: "Aktivitäten – Health & Sports", labelEn: "Activities – health & sports" },
  { value: "activities/shopping-bali", labelDe: "Aktivitäten – Shopping Bali", labelEn: "Activities – shopping Bali" },
  { value: "activities/maps-tour-routes", labelDe: "Aktivitäten – Touren-Karten", labelEn: "Activities – tour maps" },
  { value: "activities/booking", labelDe: "Aktivitäten – Buchung", labelEn: "Activities – booking" },
  { value: "weddings/weddings-gallery", labelDe: "Hochzeit – Galerie", labelEn: "Weddings – gallery" },
  { value: "weddings", labelDe: "Hochzeit – Header", labelEn: "Weddings – header" },
  { value: "weddings/general-wedding-information", labelDe: "Hochzeit – Allgemeine Infos", labelEn: "Weddings – general info" },
  { value: "weddings/vow-renewal", labelDe: "Hochzeit – Eheversprechen erneuern", labelEn: "Weddings – vow renewal" },
  { value: "weddings/wedding-anniversary", labelDe: "Hochzeit – Jubiläum", labelEn: "Weddings – anniversary" },
  { value: "weddings/wedding-with-registry-office", labelDe: "Hochzeit – mit Standesamt", labelEn: "Weddings – with registry office" },
  { value: "weddings/wedding-without-registry-office", labelDe: "Hochzeit – ohne Standesamt", labelEn: "Weddings – without registry office" },
  { value: "weddings/wedding-inquiry", labelDe: "Hochzeit – Anfrage", labelEn: "Weddings – inquiry" },
  { value: "bali-info", labelDe: "Bali-Infos – Header", labelEn: "Bali info – header" },
  { value: "bali-info/about-bali", labelDe: "Bali-Infos – Über Bali", labelEn: "Bali info – about Bali" },
  { value: "bali-info/books", labelDe: "Bali-Infos – Bücher", labelEn: "Bali info – books" },
  { value: "bali-info/general", labelDe: "Bali-Infos – Allgemein", labelEn: "Bali info – general" },
  { value: "balivwtour/bali-info/plants-and-fruits", labelDe: "Bali-Infos – Pflanzen & Früchte", labelEn: "Bali info – plants & fruits" },
  { value: "balivwtour/bali-info/spices", labelDe: "Bali-Infos – Gewürze", labelEn: "Bali info – spices" },
];

const readFileAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

export default function BackstagePage() {
  const { i18n } = useTranslation();
  const isDe = i18n.language.startsWith("de");

  const [pin, setPin] = useState("");
  const [showPin, setShowPin] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [authError, setAuthError] = useState("");
  const [authLoading, setAuthLoading] = useState(false);

  const [tab, setTab] = useState<"reservations" | "buchungen" | "blocked" | "touren" | "fotos" | "preise" | "transfers" | "captions">("reservations");
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [resFilterStatus, setResFilterStatus] = useState<"all" | "pending" | "confirmed" | "cancelled">("all");
  const [resFilterPeriod, setResFilterPeriod] = useState<"all" | "upcoming" | "past" | "custom">("all");
  const [resFilterFrom, setResFilterFrom] = useState("");
  const [resFilterTo, setResFilterTo] = useState("");
  const [blocked, setBlocked] = useState<BlockedDate[]>([]);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState("");
  const [prices, setPrices] = useState<BausteinPrice[]>([]);
  const [priceLoading, setPriceLoading] = useState(false);
  const [priceSaving, setPriceSaving] = useState(false);
  const [exchangeRate, setExchangeRate] = useState(1.0753);

  // Caption editor state
  const [captionGallery, setCaptionGallery] = useState<string>(
    GALLERY_REGISTRY[0]?.key ?? ""
  );
  const [captionOverrides, setCaptionOverrides] = useState<Record<string, string>>({});
  const [captionDraft, setCaptionDraft] = useState<Record<string, string>>({});
  const [captionsLoaded, setCaptionsLoaded] = useState(false);
  const [captionLoading, setCaptionLoading] = useState(false);
  const [captionSaving, setCaptionSaving] = useState(false);
  const [captionLang, setCaptionLang] = useState<"de" | "en">("de");

  // Edit reservation state
  const [editing, setEditing] = useState<Reservation | null>(null);
  const [editSaving, setEditSaving] = useState(false);

  // Block form state
  const [blockVilla, setBlockVilla] = useState("villa-frangipani");
  const [blockStart, setBlockStart] = useState("");
  const [blockEnd, setBlockEnd] = useState("");
  const [blockReason, setBlockReason] = useState("");

  // Photo upload state
  const [uploadFolder, setUploadFolder] = useState("home");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState("");
  const [existingImages, setExistingImages] = useState<{ name: string; path: string }[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const storedPin = typeof window !== "undefined" ? sessionStorage.getItem("villa_admin_pin") : null;

  useEffect(() => {
    if (storedPin) {
      verifyAndLoad(storedPin);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verifyAndLoad = async (thePin: string) => {
    setAuthLoading(true);
    setAuthError("");
    try {
      const { data, error } = await invokeAdmin<{
        ok?: boolean;
        secretSet?: boolean;
        secretLength?: number;
        version?: string;
      } | null>({ action: "login", pin: thePin });

      if (error) {
        const errAny = error as { message?: string; context?: unknown };
        const detail = errAny?.context
          ? ` | Details: ${JSON.stringify(errAny.context)}`
          : "";
        setAuthError(
          isDe
            ? `Backend-Fehler: ${error}${detail}`
            : `Backend error: ${error}${detail}`
        );
        sessionStorage.removeItem("villa_admin_pin");
        setAuthed(false);
        return;
      }

      const d = data as {
        ok?: boolean;
        secretSet?: boolean;
        secretLength?: number;
        version?: string;
      } | null;
      if (!d || d.ok !== true) {
        if (d && d.secretSet === false) {
          setAuthError(
            isDe
              ? "Der Admin-Code ist im Backend noch nicht hinterlegt. Bitte prüfe den Secret BACKSTAGE_PASSWORD (Project Settings → Edge Functions → Secrets)."
              : "The admin code is not stored in the backend yet. Please check the BACKSTAGE_PASSWORD secret (Project Settings → Edge Functions → Secrets)."
          );
        } else if (
          d &&
          typeof d.secretLength === "number" &&
          d.secretLength !== thePin.trim().length
        ) {
          setAuthError(
            isDe
              ? "Der gespeicherte Code enthält vermutlich zusätzliche Leerzeichen oder einen Zeilenumbruch. Bitte öffne den Secret-Wert und entferne überflüssige Zeichen."
              : "The stored code likely contains extra spaces or a line break. Please open the secret value and remove any extra characters."
          );
        } else {
          const storedLen =
            d && typeof d.secretLength === "number"
              ? String(d.secretLength)
              : "?";
          const ver = d && typeof d.version === "string" ? d.version : "keine";
          setAuthError(
            isDe
              ? `Falscher Admin-Code. Gespeicherte Länge: ${storedLen} (Backend-Version: ${ver})`
              : `Incorrect admin code. Stored length: ${storedLen} (backend version: ${ver})`
          );
        }
        sessionStorage.removeItem("villa_admin_pin");
        setAuthed(false);
        return;
      }
      sessionStorage.setItem("villa_admin_pin", thePin);
      setAuthed(true);
      await loadData(thePin);
    } catch {
      setAuthError(isDe ? "Verbindungsfehler. Bitte erneut versuchen." : "Connection error. Please try again.");
    } finally {
      setAuthLoading(false);
    }
  };

  const loadData = async (thePin: string) => {
    setLoading(true);
    try {
      const [resRes, blockRes] = await Promise.all([
        invokeAdmin<{ reservations: Reservation[] }>({ action: "list_reservations", pin: thePin }),
        invokeAdmin<{ blocked: BlockedDate[] }>({ action: "list_blocked", pin: thePin }),
      ]);
      if (resRes.data?.reservations) setReservations(resRes.data.reservations);
      if (blockRes.data?.blocked) setBlocked(blockRes.data.blocked);
    } catch {
      setNotice(isDe ? "Laden fehlgeschlagen." : "Loading failed.");
    } finally {
      setLoading(false);
    }
  };

  const loadPrices = async () => {
    setPriceLoading(true);
    try {
      const { data, error } = await invokeAdmin<{ prices: BausteinPrice[] }>({
        action: "list_prices",
        pin: storedPin,
      });
      if (!error && data?.prices) {
        const rateRow = data.prices.find(
          (p) => p.key === "eur_to_usd_rate" || p.unit === "rate"
        );
        const rate = rateRow ? Number(rateRow.amount) : NaN;
        setExchangeRate(Number.isFinite(rate) && rate > 0 ? rate : 1.0753);
        setPrices(
          data.prices.filter((p) => p.key !== "eur_to_usd_rate" && p.unit !== "rate")
        );
      }
    } catch {
      // ignore
    } finally {
      setPriceLoading(false);
    }
  };

  const updatePrice = (key: string, field: "amount" | "amount_eur", value: string) => {
    const num = value === "" ? 0 : Number(value);
    setPrices((prev) =>
      prev.map((p) =>
        p.key === key ? { ...p, [field]: Number.isFinite(num) ? num : 0 } : p
      )
    );
  };

  const handleSavePrices = async () => {
    setPriceSaving(true);
    setNotice("");
    try {
      const { error } = await invokeAdmin<{ ok?: boolean }>({
        action: "update_prices",
        pin: storedPin,
        items: [
          ...prices.map((p) => ({
            key: p.key,
            amount: p.amount,
          })),
          { key: "eur_to_usd_rate", amount: exchangeRate },
        ],
      });
      if (error) throw new Error();
      setNotice(isDe ? "Preise gespeichert." : "Prices saved.");
    } catch {
      setNotice(isDe ? "Speichern der Preise fehlgeschlagen." : "Saving prices failed.");
    } finally {
      setPriceSaving(false);
    }
  };

  const unitLabel = (unit: string) => {
    if (unit === "night") return isDe ? "/ Nacht" : "/ night";
    if (unit === "person") return isDe ? "/ Person" : "/ person";
    if (unit === "percent") return "%";
    if (unit === "nights") return isDe ? "Nächte" : "nights";
    if (unit === "excursions") return isDe ? "Tagesausflüge" : "day trips";
    if (unit === "flat") return isDe ? "pro Fahrt" : "per trip";
    return isDe ? "pro Einheit" : "per unit";
  };

  const handlePrintPrices = () => {
    const dateStr = new Date().toLocaleDateString(isDe ? "de-DE" : "en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
    const docTitle = isDe ? "Villa Kompiang — Preisliste" : "Villa Kompiang — Price list";
    const heading = isDe ? "Preisliste" : "Price list";
    const brand = "Villa Kompiang";
    const standLine = isDe ? `Stand: ${dateStr}` : `As of ${dateStr}`;
    const rateLine = isDe
      ? `Umrechnungskurs: 1 € = ${exchangeRate} US$`
      : `Exchange rate: 1 € = ${exchangeRate} US$`;
    const headService = isDe ? "Leistung" : "Service";
    const headUnit = isDe ? "Einheit" : "Unit";
    const footNote = isDe
      ? "Alle Preise in Euro (Basis). Die US-Dollar-Spalte wurde mit dem oben genannten Kurs umgerechnet und dient nur zur Orientierung."
      : "All prices in euro (base). The US dollar column was converted using the rate above and is for guidance only.";

    const groupsHtml = PRICE_GROUPS.map((group) => {
      const items = prices.filter((p) => p.category === group.category);
      if (items.length === 0) return "";
      const rows = items
        .map((p) => {
          const isCurrency =
            p.unit !== "percent" && p.unit !== "nights" && p.unit !== "excursions" && p.unit !== "rate";
          const label = escapeHtml(isDe ? p.label_de : p.label_en);
          const unit = escapeHtml(unitLabel(p.unit));
          const eurCell = isCurrency
            ? escapeHtml(formatAmount(p.amount, true))
            : `${p.amount} ${unit}`;
          const usdCell = isCurrency
            ? escapeHtml(formatAmount(toUsd(p.amount, exchangeRate), false))
            : "—";
          return `<tr><td>${label}</td><td class="unit">${unit}</td><td class="num">${eurCell}</td><td class="num muted">${usdCell}</td></tr>`;
        })
        .join("");
      return `<section><h2>${escapeHtml(isDe ? group.labelDe : group.labelEn)}</h2><table><thead><tr><th>${headService}</th><th>${headUnit}</th><th class="num">€</th><th class="num">US$</th></tr></thead><tbody>${rows}</tbody></table></section>`;
    }).join("");

    const html = `<!DOCTYPE html><html lang="${isDe ? "de" : "en"}"><head><meta charset="utf-8" /><title>${escapeHtml(docTitle)}</title><style>
      @page { size: A4; margin: 18mm 16mm; }
      * { box-sizing: border-box; }
      body { margin: 0; font-family: Georgia, 'Times New Roman', serif; color: #2b2622; background: #fff; font-size: 12px; line-height: 1.5; }
      header { border-bottom: 2px solid #b08968; padding-bottom: 14px; margin-bottom: 22px; }
      .brand { font-size: 11px; letter-spacing: .18em; text-transform: uppercase; color: #8a6d52; margin: 0 0 4px; }
      h1 { font-size: 26px; margin: 0 0 6px; color: #2b2622; }
      .meta { font-size: 12px; color: #6b6259; margin: 0; }
      .meta + .meta { margin-top: 2px; }
      section { margin-bottom: 20px; break-inside: avoid; }
      h2 { font-size: 14px; margin: 0 0 8px; padding: 7px 10px; background: #f5efe8; border-left: 3px solid #b08968; color: #2b2622; }
      table { width: 100%; border-collapse: collapse; }
      th, td { text-align: left; padding: 6px 8px; border-bottom: 1px solid #e8e1d9; font-size: 12px; }
      th { font-size: 10px; letter-spacing: .08em; text-transform: uppercase; color: #8a6d52; border-bottom: 1.5px solid #d9cfc3; }
      .num { text-align: right; white-space: nowrap; }
      .unit { color: #6b6259; font-size: 11px; white-space: nowrap; }
      .muted { color: #6b6259; }
      tbody tr:last-child td { border-bottom: none; }
      footer { margin-top: 24px; padding-top: 12px; border-top: 1px solid #e8e1d9; font-size: 10.5px; color: #6b6259; }
    </style></head><body>
      <header>
        <p class="brand">${escapeHtml(brand)}</p>
        <h1>${escapeHtml(heading)}</h1>
        <p class="meta">${escapeHtml(standLine)}</p>
        <p class="meta">${escapeHtml(rateLine)}</p>
      </header>
      ${groupsHtml}
      <footer>${escapeHtml(footNote)}</footer>
    </body></html>`;

    const printWindow = window.open("", "_blank", "width=920,height=1200");
    if (!printWindow) {
      setNotice(
        isDe
          ? "Bitte erlaube Pop-ups für diese Seite, um die PDF-Ansicht zu öffnen."
          : "Please allow pop-ups for this page to open the PDF view."
      );
      return;
    }
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 350);
  };

  const statusLabel = (status: string) => {
    const map: Record<string, { de: string; en: string }> = {
      pending: { de: "Offen", en: "Pending" },
      confirmed: { de: "Bestätigt", en: "Confirmed" },
      cancelled: { de: "Storniert", en: "Cancelled" },
    };
    const entry = map[status];
    if (entry) return isDe ? entry.de : entry.en;
    return status;
  };

  const nightsBetween = (checkIn: string, checkOut: string) => {
    const start = new Date(checkIn).getTime();
    const end = new Date(checkOut).getTime();
    if (!Number.isFinite(start) || !Number.isFinite(end)) return 0;
    return Math.max(0, Math.round((end - start) / 86400000));
  };

  const handlePrintReservations = () => {
    const dateStr = new Date().toLocaleDateString(isDe ? "de-DE" : "en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
    const docTitle = isDe ? "Villa Kompiang — Reservierungen" : "Villa Kompiang — Reservations";
    const heading = isDe ? "Reservierungsliste" : "Reservation list";
    const brand = "Villa Kompiang";
    const standLine = isDe ? `Stand: ${dateStr}` : `As of ${dateStr}`;
    const countLine = isDe
      ? `${filteredReservations.length} Reservierung(en)`
      : `${filteredReservations.length} reservation(s)`;
    const periodLabel =
      resFilterPeriod === "upcoming"
        ? isDe
          ? "Kommende Anreisen (ab heute)"
          : "Upcoming arrivals (from today)"
        : resFilterPeriod === "past"
        ? isDe
          ? "Vergangene Anreisen (vor heute)"
          : "Past arrivals (before today)"
        : resFilterPeriod === "custom"
        ? resFilterFrom || resFilterTo
          ? `${resFilterFrom ? formatDate(resFilterFrom) : "…"} – ${resFilterTo ? formatDate(resFilterTo) : "…"}`
          : isDe
          ? "Benutzerdefinierter Zeitraum"
          : "Custom range"
        : isDe
        ? "Alle Zeiträume"
        : "All periods";
    const statusFilterLabel =
      resFilterStatus === "all"
        ? isDe
          ? "Alle Status"
          : "All statuses"
        : statusLabel(resFilterStatus);
    const filterLine = `${isDe ? "Filter" : "Filter"}: ${periodLabel} · ${statusFilterLabel}`;
    const cols = isDe
      ? ["Buchungs-Nr.", "Gast", "Villa", "Anreise", "Abreise", "Nächte", "Erw.", "Kinder", "E-Mail", "Telefon", "Status"]
      : ["Booking no.", "Guest", "Villa", "Check-in", "Check-out", "Nights", "Adults", "Children", "Email", "Phone", "Status"];
    const footNote = isDe
      ? "Diese Liste wird automatisch aus dem Backstage erzeugt. Änderungen an Reservierungen sind dort jederzeit möglich."
      : "This list is generated automatically from the backstage. Reservations can be edited there at any time.";

    const headHtml = cols.map((c) => `<th>${escapeHtml(c)}</th>`).join("");

    const rows = filteredReservations
      .map((r) => {
        const nights = nightsBetween(r.check_in, r.check_out);
        return `<tr>
          <td class="mono">${escapeHtml(r.booking_no || "—")}</td>
          <td>${escapeHtml(`${r.firstname} ${r.lastname}`.trim())}</td>
          <td>${escapeHtml(villaName(r.villa_slug))}</td>
          <td class="nowrap">${escapeHtml(formatDate(r.check_in))}</td>
          <td class="nowrap">${escapeHtml(formatDate(r.check_out))}</td>
          <td class="num">${nights}</td>
          <td class="num">${r.adults}</td>
          <td class="num">${r.children}</td>
          <td>${escapeHtml(r.email)}</td>
          <td class="nowrap">${escapeHtml(r.phone || "—")}</td>
          <td>${escapeHtml(statusLabel(r.status))}</td>
        </tr>`;
      })
      .join("");

    const totalNights = filteredReservations.reduce(
      (sum, r) => sum + nightsBetween(r.check_in, r.check_out),
      0
    );
    const totalAdults = filteredReservations.reduce((sum, r) => sum + (r.adults || 0), 0);
    const totalChildren = filteredReservations.reduce(
      (sum, r) => sum + (r.children || 0),
      0
    );
    const totalGuests = totalAdults + totalChildren;
    const totalResCount = filteredReservations.length;
    const statTotalsLabel = isDe ? "Summe" : "Totals";
    const statReservationsLabel = isDe ? "Reservierungen" : "Reservations";
    const statNightsLabel = isDe ? "Nächte insgesamt" : "Nights total";
    const statAdultsLabel = isDe ? "Erwachsene" : "Adults";
    const statChildrenLabel = isDe ? "Kinder" : "Children";
    const statGuestsLabel = isDe ? "Gäste insgesamt" : "Guests total";
    const totalsHtml =
      totalResCount > 0
        ? `<div class="totals">
            <span class="totals-title">${escapeHtml(statTotalsLabel)}</span>
            <span class="stat"><span class="stat-label">${escapeHtml(statReservationsLabel)}</span><span class="stat-value">${totalResCount}</span></span>
            <span class="stat"><span class="stat-label">${escapeHtml(statNightsLabel)}</span><span class="stat-value">${totalNights}</span></span>
            <span class="stat"><span class="stat-label">${escapeHtml(statAdultsLabel)}</span><span class="stat-value">${totalAdults}</span></span>
            <span class="stat"><span class="stat-label">${escapeHtml(statChildrenLabel)}</span><span class="stat-value">${totalChildren}</span></span>
            <span class="stat stat-strong"><span class="stat-label">${escapeHtml(statGuestsLabel)}</span><span class="stat-value">${totalGuests}</span></span>
          </div>`
        : "";

    const html = `<!DOCTYPE html><html lang="${isDe ? "de" : "en"}"><head><meta charset="utf-8" /><title>${escapeHtml(docTitle)}</title><style>
      @page { size: A4 landscape; margin: 14mm 12mm; }
      * { box-sizing: border-box; }
      body { margin: 0; font-family: Georgia, 'Times New Roman', serif; color: #2b2622; background: #fff; font-size: 11px; line-height: 1.4; }
      header { border-bottom: 2px solid #b08968; padding-bottom: 12px; margin-bottom: 18px; }
      .brand { font-size: 10px; letter-spacing: .18em; text-transform: uppercase; color: #8a6d52; margin: 0 0 4px; }
      h1 { font-size: 22px; margin: 0 0 6px; color: #2b2622; }
      .meta { font-size: 11px; color: #6b6259; margin: 0; }
      table { width: 100%; border-collapse: collapse; }
      th, td { text-align: left; padding: 5px 7px; border-bottom: 1px solid #e8e1d9; vertical-align: top; }
      th { font-size: 9.5px; letter-spacing: .06em; text-transform: uppercase; color: #8a6d52; border-bottom: 1.5px solid #d9cfc3; }
      .num { text-align: right; }
      .mono { font-family: 'Courier New', monospace; font-size: 10px; white-space: nowrap; }
      .nowrap { white-space: nowrap; }
      tbody tr:nth-child(even) { background: #faf7f3; }
      .totals { margin-top: 16px; padding: 12px 14px; background: #f5efe8; border: 1px solid #e8e1d9; border-left: 3px solid #b08968; display: flex; flex-wrap: wrap; align-items: baseline; gap: 8px 26px; break-inside: avoid; }
      .totals-title { font-size: 10px; letter-spacing: .18em; text-transform: uppercase; color: #8a6d52; width: 100%; margin-bottom: 2px; }
      .stat { display: flex; flex-direction: column; }
      .stat-label { font-size: 9.5px; letter-spacing: .05em; text-transform: uppercase; color: #8a6d52; }
      .stat-value { font-size: 17px; color: #2b2622; }
      .stat-strong .stat-value { font-weight: bold; color: #6b4f34; }
      footer { margin-top: 20px; padding-top: 10px; border-top: 1px solid #e8e1d9; font-size: 10px; color: #6b6259; }
    </style></head><body>
      <header>
        <p class="brand">${escapeHtml(brand)}</p>
        <h1>${escapeHtml(heading)}</h1>
        <p class="meta">${escapeHtml(standLine)} · ${escapeHtml(countLine)}</p>
        <p class="meta">${escapeHtml(filterLine)}</p>
      </header>
      <table><thead><tr>${headHtml}</tr></thead><tbody>${rows}</tbody></table>
      ${totalsHtml}
      <footer>${escapeHtml(footNote)}</footer>
    </body></html>`;

    const printWindow = window.open("", "_blank", "width=1200,height=900");
    if (!printWindow) {
      setNotice(
        isDe
          ? "Bitte erlaube Pop-ups für diese Seite, um die PDF-Ansicht zu öffnen."
          : "Please allow pop-ups for this page to open the PDF view."
      );
      return;
    }
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 350);
  };

  const overrideKey = (galleryKey: string, imageKey: string) =>
    `${galleryKey}||${imageKey}`;

  // Standard-Beschriftung je Sprache aus dem Code. Englisch nutzt den
  // englischen Standard (captionEn), Deutsch den deutschen (caption).
  const defaultForLang = (
    image: { caption: string; captionEn?: string },
    lang: "de" | "en"
  ) => (lang === "en" ? image.captionEn ?? image.caption : image.caption);

  const loadCaptions = async () => {
    setCaptionLoading(true);
    try {
      const { data, error } = await invokeAdmin<{
        captions: { gallery_key: string; image_key: string; caption: string }[];
      }>({ action: "list_captions", pin: storedPin });
      if (error) throw new Error(error);
      const map: Record<string, string> = {};
      (data?.captions || []).forEach((row) => {
        map[overrideKey(row.gallery_key, row.image_key)] = row.caption;
      });
      setCaptionOverrides(map);
    } catch {
      setCaptionOverrides({});
    } finally {
      setCaptionLoading(false);
      setCaptionsLoaded(true);
    }
  };

  const handleSaveCaptions = async () => {
    const def = getGalleryDef(captionGallery);
    if (!def) return;
    const scopedKey = galleryKeyForLang(captionGallery, captionLang);
    setCaptionSaving(true);
    setNotice("");
    try {
      const items = def.images
        .map((image) => {
          const draftVal = captionDraft[image.key] ?? "";
          const original =
            captionOverrides[overrideKey(scopedKey, image.key)] ??
            defaultForLang(image, captionLang);
          return { imageKey: image.key, draftVal, original };
        })
        .filter(({ draftVal, original }) => draftVal !== original)
        .map(({ imageKey, draftVal }) => ({ image_key: imageKey, caption: draftVal }));

      if (items.length === 0) {
        setNotice(isDe ? "Keine Änderungen zum Speichern." : "No changes to save.");
        setCaptionSaving(false);
        return;
      }

      const { error } = await invokeAdmin<{ ok?: boolean }>({
        action: "update_captions",
        pin: storedPin,
        gallery_key: scopedKey,
        items,
      });
      if (error) throw new Error(error);
      setNotice(isDe ? "Beschriftungen gespeichert." : "Captions saved.");
      await loadCaptions();
    } catch {
      setNotice(isDe ? "Speichern der Beschriftungen fehlgeschlagen." : "Saving captions failed.");
    } finally {
      setCaptionSaving(false);
    }
  };

  const resetCaptionRow = (imageKey: string) => {
    setCaptionDraft((prev) => ({ ...prev, [imageKey]: "" }));
  };

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();
    await verifyAndLoad(pin);
  };

  const handleBlock = async (e: FormEvent) => {
    e.preventDefault();
    if (!blockStart || !blockEnd) return;
    setNotice("");
    try {
      const { data, error } = await invokeAdmin<{ error?: string }>({
        action: "block_dates",
        pin: storedPin,
        villa_slug: blockVilla,
        start: blockStart,
        end: blockEnd,
        reason: blockReason,
      });
      if (error) throw new Error((data as { error?: string })?.error || "error");
      setNotice(isDe ? "Tage erfolgreich gesperrt." : "Days successfully blocked.");
      setBlockStart("");
      setBlockEnd("");
      setBlockReason("");
      await loadData(storedPin || "");
    } catch {
      setNotice(isDe ? "Sperren fehlgeschlagen." : "Blocking failed.");
    }
  };

  const handleUnblock = async (id: string) => {
    setNotice("");
    try {
      const { error } = await invokeAdmin<{ error?: string }>({ action: "unblock_id", pin: storedPin, id });
      if (error) throw new Error();
      setNotice(isDe ? "Tag freigegeben." : "Day unblocked.");
      await loadData(storedPin || "");
    } catch {
      setNotice(isDe ? "Freigeben fehlgeschlagen." : "Unblocking failed.");
    }
  };

  const handleDeleteReservation = async (id: string) => {
    if (!window.confirm(isDe ? "Reservierung wirklich löschen?" : "Really delete this reservation?")) return;
    setNotice("");
    try {
      const { error } = await invokeAdmin<{ error?: string }>({ action: "delete_reservation", pin: storedPin, id });
      if (error) throw new Error();
      setNotice(isDe ? "Reservierung gelöscht." : "Reservation deleted.");
      await loadData(storedPin || "");
    } catch {
      setNotice(isDe ? "Löschen fehlgeschlagen." : "Deletion failed.");
    }
  };

  const handleDeleteImage = async (path: string, name: string) => {
    if (!window.confirm(isDe ? `Bild „${name}" wirklich löschen?` : `Really delete image "${name}"?`)) return;
    setNotice("");
    try {
      const { error } = await invokeAdmin<{ ok?: boolean }>({
        action: "delete_image",
        pin: storedPin,
        path,
      });
      if (error) throw new Error();
      setNotice(isDe ? `Bild „${name}" gelöscht.` : `Image "${name}" deleted.`);
      await loadExistingImages(uploadFolder);
    } catch {
      setNotice(isDe ? "Löschen fehlgeschlagen." : "Deletion failed.");
    }
  };

  const handleFilesSelected = (files: FileList | File[]) => {
    const list = Array.from(files);
    const imageFiles = list.filter((f) => f.type.startsWith("image/"));
    setSelectedFiles((prev) => [...prev, ...imageFiles]);
    setUploadResult("");
  };

  const removeSelectedFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const clearSelectedFiles = () => {
    setSelectedFiles([]);
  };

  const loadExistingImages = async (folder: string) => {
    setListLoading(true);
    try {
      const { data, error } = await invokeAdmin<{ files: { name: string; path: string }[] }>({
        action: "list_images",
        pin: storedPin,
        folder,
      });
      if (!error && data?.files) {
        setExistingImages(data.files);
      } else {
        setExistingImages([]);
      }
    } catch {
      setExistingImages([]);
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    if (authed && tab === "fotos") {
      loadExistingImages(uploadFolder);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, tab, uploadFolder]);

  useEffect(() => {
    if (authed && tab === "preise") {
      loadPrices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, tab]);

  useEffect(() => {
    if (authed && tab === "captions" && !captionsLoaded) {
      loadCaptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, tab, captionsLoaded]);

  useEffect(() => {
    const def = getGalleryDef(captionGallery);
    if (!def) return;
    const scopedKey = galleryKeyForLang(captionGallery, captionLang);
    const next: Record<string, string> = {};
    def.images.forEach((image) => {
      next[image.key] =
        captionOverrides[overrideKey(scopedKey, image.key)] ??
        defaultForLang(image, captionLang);
    });
    setCaptionDraft(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captionGallery, captionLang, captionOverrides]);

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;
    setUploading(true);
    setUploadResult("");
    let ok = 0;
    let fail = 0;
    const errors: string[] = [];
    for (const file of selectedFiles) {
      try {
        const dataUrl = await readFileAsDataUrl(file);
        const { data, error } = await invokeAdmin<{ ok?: boolean; path?: string; error?: string }>({
          action: "upload_image",
          pin: storedPin,
          folder: uploadFolder,
          filename: file.name,
          data: dataUrl,
        });
        if (error || !data?.ok) {
          fail += 1;
          errors.push(`${file.name}: ${error || (data as { error?: string })?.error || "error"}`);
        } else {
          ok += 1;
        }
      } catch {
        fail += 1;
        errors.push(`${file.name}: failed`);
      }
    }
    if (ok > 0 || fail > 0) {
      setUploadResult(
        isDe
          ? `${ok} hochgeladen${fail > 0 ? `, ${fail} fehlgeschlagen` : ""}.`
          : `${ok} uploaded${fail > 0 ? `, ${fail} failed` : ""}.`
      );
    }
    if (errors.length > 0) {
      setUploadResult((prev) => `${prev} ${errors.slice(0, 3).join("; ")}`);
    }
    if (ok > 0) {
      setSelectedFiles([]);
      await loadExistingImages(uploadFolder);
    }
    setUploading(false);
  };

  const openEdit = (r: Reservation) => {
    setNotice("");
    setEditing({ ...r });
  };

  const closeEdit = () => {
    setEditing(null);
    setEditSaving(false);
  };

  const updateEditField = <K extends keyof Reservation>(key: K, value: Reservation[K]) => {
    setEditing((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const handleSaveEdit = async (e: FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    setEditSaving(true);
    setNotice("");
    try {
      const { data, error } = await invokeAdmin<{ error?: string }>({
        action: "update_reservation",
        pin: storedPin,
        id: editing.id,
        villa_slug: editing.villa_slug,
        check_in: editing.check_in,
        check_out: editing.check_out,
        adults: editing.adults,
        children: editing.children,
        firstname: editing.firstname,
        lastname: (editing.lastname || "").toUpperCase(),
        email: editing.email,
        phone: editing.phone,
        nationality: editing.nationality,
        message: editing.message,
      });
      if (error) throw new Error((data as { error?: string })?.error || "error");
      setNotice(isDe ? "Reservierung aktualisiert." : "Reservation updated.");
      closeEdit();
      await loadData(storedPin || "");
    } catch {
      setNotice(isDe ? "Speichern fehlgeschlagen." : "Saving failed.");
    } finally {
      setEditSaving(false);
    }
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString(isDe ? "de-DE" : "en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const captionDef = getGalleryDef(captionGallery);
  const captionScopedKey = galleryKeyForLang(captionGallery, captionLang);

  const nowForFilter = new Date();
  const todayNum = Date.UTC(
    nowForFilter.getFullYear(),
    nowForFilter.getMonth(),
    nowForFilter.getDate()
  );
  const fromNum = resFilterFrom ? dayNum(resFilterFrom) : NaN;
  const toNum = resFilterTo ? dayNum(resFilterTo) : NaN;

  const filteredReservations = reservations.filter((r) => {
    if (resFilterStatus !== "all" && r.status !== resFilterStatus) return false;
    if (resFilterPeriod === "all") return true;
    const checkIn = dayNum(r.check_in);
    if (!Number.isFinite(checkIn)) return false;
    if (resFilterPeriod === "upcoming") return checkIn >= todayNum;
    if (resFilterPeriod === "past") return checkIn < todayNum;
    if (Number.isFinite(fromNum) && checkIn < fromNum) return false;
    if (Number.isFinite(toNum) && checkIn > toNum) return false;
    return true;
  });

  const hasActiveFilter =
    resFilterStatus !== "all" || resFilterPeriod !== "all";

  const resetResFilters = () => {
    setResFilterStatus("all");
    setResFilterPeriod("all");
    setResFilterFrom("");
    setResFilterTo("");
  };

  if (!authed) {
    return (
      <div className="bg-background-50 text-foreground-950 min-h-screen flex items-center justify-center px-4">
        <div className="w-full max-w-sm bg-background-50 border border-background-200 rounded-lg p-8">
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 mb-4">
            <i className="ri-shield-check-line text-2xl"></i>
          </div>
          <h1 className="font-heading text-2xl text-foreground-950 mb-1">
            {isDe ? "Backstage" : "Backstage"}
          </h1>
          <p className="text-sm text-foreground-600 mb-6">
            {isDe
              ? "Bitte geben Sie Ihren Admin-Code ein."
              : "Please enter your admin code."}
          </p>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="relative">
              <input
                type={showPin ? "text" : "password"}
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                placeholder={isDe ? "Admin-Code" : "Admin code"}
                autoFocus
                className="w-full px-4 py-3 pr-11 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-colors"
              />
              <button
                type="button"
                onClick={() => setShowPin((s) => !s)}
                className="absolute right-0 top-0 h-full w-11 flex items-center justify-center text-foreground-500 hover:text-foreground-950 cursor-pointer transition-colors"
                aria-label={showPin ? (isDe ? "Code verbergen" : "Hide code") : (isDe ? "Code anzeigen" : "Show code")}
              >
                <i className={showPin ? "ri-eye-off-line text-lg" : "ri-eye-line text-lg"}></i>
              </button>
            </div>
            {authError && (
              <p className="text-sm text-secondary-700 bg-secondary-100 border border-secondary-200 rounded-md px-4 py-3">
                {authError}
              </p>
            )}
            <button
              type="submit"
              disabled={authLoading}
              className="w-full inline-flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-60 text-background-50 text-sm font-medium px-6 py-3 rounded-md whitespace-nowrap cursor-pointer transition-colors"
            >
              {authLoading && <i className="ri-loader-4-line animate-spin"></i>}
              {isDe ? "Anmelden" : "Sign in"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background-50 text-foreground-950 min-h-screen">
      <header className="bg-background-100 border-b border-background-200">
        <div className="max-w-6xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 flex items-center justify-center rounded-md bg-accent-500 text-background-50">
              <i className="ri-plant-line text-lg"></i>
            </div>
            <span className="font-heading text-lg text-foreground-950">
              {isDe ? "Villa Kompiang — Backstage" : "Villa Kompiang — Backstage"}
            </span>
          </div>
          <button
            onClick={() => {
              sessionStorage.removeItem("villa_admin_pin");
              setAuthed(false);
              setPin("");
            }}
            className="text-sm text-foreground-600 hover:text-foreground-950 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-logout-box-r-line mr-1.5"></i>
            {isDe ? "Abmelden" : "Log out"}
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-8 py-8">
        {/* Tabs */}
        <div className="inline-flex flex-wrap items-center rounded-full bg-background-100 border border-background-200 p-1 mb-8">
          <button
            onClick={() => setTab("reservations")}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
              tab === "reservations"
                ? "bg-accent-500 text-background-50"
                : "text-foreground-700 hover:text-foreground-950"
            }`}
          >
            {isDe ? "Reservierungen" : "Reservations"}
          </button>
          <button
            onClick={() => setTab("buchungen")}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
              tab === "buchungen"
                ? "bg-accent-500 text-background-50"
                : "text-foreground-700 hover:text-foreground-950"
            }`}
          >
            {isDe ? "Buchungen" : "Bookings"}
          </button>
          <button
            onClick={() => setTab("blocked")}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
              tab === "blocked"
                ? "bg-accent-500 text-background-50"
                : "text-foreground-700 hover:text-foreground-950"
            }`}
          >
            {isDe ? "Tage sperren" : "Block days"}
          </button>
          <button
            onClick={() => setTab("touren")}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
              tab === "touren"
                ? "bg-accent-500 text-background-50"
                : "text-foreground-700 hover:text-foreground-950"
            }`}
          >
            {isDe ? "Tour-Kalender" : "Tour calendar"}
          </button>
          <button
            onClick={() => setTab("fotos")}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
              tab === "fotos"
                ? "bg-accent-500 text-background-50"
                : "text-foreground-700 hover:text-foreground-950"
            }`}
          >
            {isDe ? "Fotos" : "Photos"}
          </button>
          <button
            onClick={() => setTab("preise")}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
              tab === "preise"
                ? "bg-accent-500 text-background-50"
                : "text-foreground-700 hover:text-foreground-950"
            }`}
          >
            {isDe ? "Preise" : "Prices"}
          </button>
          <button
            onClick={() => setTab("transfers")}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
              tab === "transfers"
                ? "bg-accent-500 text-background-50"
                : "text-foreground-700 hover:text-foreground-950"
            }`}
          >
            {isDe ? "Transfers" : "Transfers"}
          </button>
          <button
            onClick={() => setTab("captions")}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
              tab === "captions"
                ? "bg-accent-500 text-background-50"
                : "text-foreground-700 hover:text-foreground-950"
            }`}
          >
            {isDe ? "Beschriftungen" : "Captions"}
          </button>
        </div>

        {notice && (
          <div className="mb-6 text-sm font-medium text-primary-700 bg-primary-100 border border-primary-200 rounded-md px-4 py-3">
            {notice}
          </div>
        )}

        {loading && (
          <div className="flex items-center gap-2 text-sm text-foreground-600 mb-6">
            <i className="ri-loader-4-line animate-spin"></i>
            {isDe ? "Wird geladen..." : "Loading..."}
          </div>
        )}

        {tab === "reservations" && (
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
              <h2 className="font-heading text-2xl text-foreground-950">
                {isDe ? "Eingehende Reservierungen" : "Incoming reservations"}
              </h2>
              <button
                onClick={handlePrintReservations}
                disabled={loading || filteredReservations.length === 0}
                className="inline-flex items-center justify-center gap-2 bg-background-100 hover:bg-background-200 disabled:opacity-60 text-foreground-800 border border-background-300 text-sm font-medium px-5 py-2.5 rounded-md whitespace-nowrap cursor-pointer transition-colors shrink-0"
              >
                <i className="ri-file-pdf-2-line"></i>
                {isDe ? "Als PDF drucken" : "Print as PDF"}
              </button>
            </div>

            <div className="bg-background-50 border border-background-200 rounded-lg p-4 mb-6">
              <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-end gap-4">
                <div className="w-full sm:w-52">
                  <label className="block text-xs font-medium text-foreground-600 mb-1.5">
                    {isDe ? "Status" : "Status"}
                  </label>
                  <select
                    value={resFilterStatus}
                    onChange={(e) =>
                      setResFilterStatus(
                        e.target.value as "all" | "pending" | "confirmed" | "cancelled"
                      )
                    }
                    className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
                  >
                    <option value="all">{isDe ? "Alle Status" : "All statuses"}</option>
                    <option value="pending">{isDe ? "Offen" : "Pending"}</option>
                    <option value="confirmed">{isDe ? "Bestätigt" : "Confirmed"}</option>
                    <option value="cancelled">{isDe ? "Storniert" : "Cancelled"}</option>
                  </select>
                </div>
                <div className="w-full sm:w-60">
                  <label className="block text-xs font-medium text-foreground-600 mb-1.5">
                    {isDe ? "Zeitraum (Anreise)" : "Period (check-in)"}
                  </label>
                  <select
                    value={resFilterPeriod}
                    onChange={(e) =>
                      setResFilterPeriod(
                        e.target.value as "all" | "upcoming" | "past" | "custom"
                      )
                    }
                    className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
                  >
                    <option value="all">{isDe ? "Alle Zeiträume" : "All periods"}</option>
                    <option value="upcoming">
                      {isDe ? "Kommende Anreisen" : "Upcoming arrivals"}
                    </option>
                    <option value="past">
                      {isDe ? "Vergangene Anreisen" : "Past arrivals"}
                    </option>
                    <option value="custom">
                      {isDe ? "Benutzerdefiniert…" : "Custom range…"}
                    </option>
                  </select>
                </div>
                {resFilterPeriod === "custom" && (
                  <>
                    <div className="w-full sm:w-44">
                      <label className="block text-xs font-medium text-foreground-600 mb-1.5">
                        {isDe ? "Von" : "From"}
                      </label>
                      <DatePicker
                        value={resFilterFrom}
                        onChange={setResFilterFrom}
                        isDe={isDe}
                        placeholder={isDe ? "Datum wählen" : "Select date"}
                      />
                    </div>
                    <div className="w-full sm:w-44">
                      <label className="block text-xs font-medium text-foreground-600 mb-1.5">
                        {isDe ? "Bis" : "To"}
                      </label>
                      <DatePicker
                        value={resFilterTo}
                        onChange={setResFilterTo}
                        min={resFilterFrom || undefined}
                        isDe={isDe}
                        placeholder={isDe ? "Datum wählen" : "Select date"}
                      />
                    </div>
                  </>
                )}
                {hasActiveFilter && (
                  <button
                    type="button"
                    onClick={resetResFilters}
                    className="inline-flex items-center justify-center gap-1.5 bg-background-100 hover:bg-background-200 text-foreground-700 border border-background-300 text-sm font-medium px-4 py-2.5 rounded-md whitespace-nowrap cursor-pointer transition-colors shrink-0"
                  >
                    <i className="ri-close-line"></i>
                    {isDe ? "Zurücksetzen" : "Reset"}
                  </button>
                )}
              </div>
              <p className="mt-3 text-xs text-foreground-500">
                {isDe
                  ? `${filteredReservations.length} von ${reservations.length} Reservierungen`
                  : `${filteredReservations.length} of ${reservations.length} reservations`}
              </p>
            </div>

            {filteredReservations.length === 0 && !loading ? (
              <div className="bg-background-100 border border-background-200 rounded-lg p-10 text-center text-foreground-600">
                {reservations.length === 0
                  ? isDe
                    ? "Noch keine Reservierungen vorhanden."
                    : "No reservations yet."
                  : isDe
                  ? "Keine Reservierungen entsprechen diesem Filter."
                  : "No reservations match this filter."}
              </div>
            ) : (
              <div className="space-y-4">
                {filteredReservations.map((r) => (
                  <div
                    key={r.id}
                    className="bg-background-50 border border-background-200 rounded-lg p-5"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-heading text-lg text-foreground-950">
                            {r.firstname} {r.lastname.toUpperCase()}
                          </span>
                          <span className="text-xs bg-accent-100 text-accent-800 px-2 py-0.5 rounded-full whitespace-nowrap">
                            {villaName(r.villa_slug)}
                          </span>
                          {r.booking_no && (
                            <span className="text-xs bg-accent-100 text-accent-800 px-2 py-0.5 rounded-full whitespace-nowrap font-mono">
                              {r.booking_no}
                            </span>
                          )}
                        </div>
                        <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-foreground-700">
                          <span>
                            <i className="ri-calendar-line text-accent-600 mr-1"></i>
                            {formatDate(r.check_in)} — {formatDate(r.check_out)}
                          </span>
                          <span>
                            <i className="ri-user-line text-accent-600 mr-1"></i>
                            {isDe ? `${r.adults} Erwachsene, ${r.children} Kinder` : `${r.adults} adults, ${r.children} children`}
                          </span>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-foreground-700">
                          <span>
                            <i className="ri-mail-line text-accent-600 mr-1"></i>
                            {r.email}
                          </span>
                          {r.phone && (
                            <span>
                              <i className="ri-phone-line text-accent-600 mr-1"></i>
                              {r.phone}
                            </span>
                          )}
                          {r.nationality && (
                            <span>
                              <i className="ri-global-line text-accent-600 mr-1"></i>
                              {r.nationality}
                            </span>
                          )}
                        </div>
                        {r.message && (
                          <p className="mt-3 text-sm text-foreground-600 bg-background-100 rounded-md px-3 py-2">
                            {r.message}
                          </p>
                        )}
                        <div className="mt-2 text-xs text-foreground-500">
                          {isDe ? "Eingegangen am" : "Received on"}{" "}
                          {new Date(r.created_at).toLocaleString(isDe ? "de-DE" : "en-GB")}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          onClick={() => openEdit(r)}
                          className="inline-flex items-center gap-1.5 text-xs text-foreground-700 bg-background-100 hover:bg-background-200 border border-background-200 px-3 py-1.5 rounded-md whitespace-nowrap cursor-pointer transition-colors"
                        >
                          <i className="ri-edit-line"></i>
                          {isDe ? "Bearbeiten" : "Edit"}
                        </button>
                        <button
                          onClick={() => handleDeleteReservation(r.id)}
                          className="inline-flex items-center gap-1.5 text-xs text-secondary-700 bg-secondary-100 hover:bg-secondary-200 border border-secondary-200 px-3 py-1.5 rounded-md whitespace-nowrap cursor-pointer transition-colors"
                        >
                          <i className="ri-delete-bin-line"></i>
                          {isDe ? "Löschen" : "Delete"}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "buchungen" && (
          <BausteinBookings pin={storedPin} isDe={isDe} />
        )}

        {tab === "blocked" && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Block form */}
            <div className="lg:col-span-1">
              <h2 className="font-heading text-xl text-foreground-950 mb-4">
                {isDe ? "Tage sperren" : "Block days"}
              </h2>
              <form onSubmit={handleBlock} className="space-y-4 bg-background-50 border border-background-200 rounded-lg p-5">
                <div>
                  <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                    {isDe ? "Villa" : "Villa"}
                  </label>
                  <select
                    value={blockVilla}
                    onChange={(e) => setBlockVilla(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
                  >
                    {VILLAS.map((v) => (
                      <option key={v.slug} value={v.slug}>
                        {v.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                    {isDe ? "Von" : "From"}
                  </label>
                  <DatePicker
                    value={blockStart}
                    onChange={setBlockStart}
                    isDe={isDe}
                    placeholder={isDe ? "Datum wählen" : "Select date"}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                    {isDe ? "Bis" : "To"}
                  </label>
                  <DatePicker
                    value={blockEnd}
                    onChange={setBlockEnd}
                    min={blockStart || undefined}
                    isDe={isDe}
                    placeholder={isDe ? "Datum wählen" : "Select date"}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                    {isDe ? "Grund (optional)" : "Reason (optional)"}
                  </label>
                  <input
                    type="text"
                    value={blockReason}
                    onChange={(e) => setBlockReason(e.target.value)}
                    placeholder={isDe ? "z. B. Wartung" : "e.g. maintenance"}
                    className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  />
                </div>
                <button
                  type="submit"
                  className="w-full inline-flex items-center justify-center gap-2 bg-accent-500 hover:bg-accent-600 text-background-50 text-sm font-medium px-6 py-2.5 rounded-md whitespace-nowrap cursor-pointer transition-colors"
                >
                  <i className="ri-lock-line"></i>
                  {isDe ? "Tage sperren" : "Block days"}
                </button>
              </form>
            </div>

            {/* Blocked list */}
            <div className="lg:col-span-2">
              <h2 className="font-heading text-xl text-foreground-950 mb-4">
                {isDe ? "Gesperrte Tage" : "Blocked days"}
              </h2>
              {blocked.length === 0 && !loading ? (
                <div className="bg-background-100 border border-background-200 rounded-lg p-10 text-center text-foreground-600">
                  {isDe ? "Keine gesperrten Tage." : "No blocked days."}
                </div>
              ) : (
                <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
                  <div className="max-h-[560px] overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-background-100 text-foreground-700 text-left sticky top-0">
                        <tr>
                          <th className="px-4 py-3 font-medium">{isDe ? "Villa" : "Villa"}</th>
                          <th className="px-4 py-3 font-medium">{isDe ? "Datum" : "Date"}</th>
                          <th className="px-4 py-3 font-medium">{isDe ? "Grund" : "Reason"}</th>
                          <th className="px-4 py-3 font-medium text-right"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {blocked.map((b) => (
                          <tr key={b.id} className="border-t border-background-100">
                            <td className="px-4 py-2.5 text-foreground-800">{villaName(b.villa_slug)}</td>
                            <td className="px-4 py-2.5 text-foreground-800">{formatDate(b.date)}</td>
                            <td className="px-4 py-2.5 text-foreground-600">{b.reason || "—"}</td>
                            <td className="px-4 py-2.5 text-right">
                              <button
                                onClick={() => handleUnblock(b.id)}
                                className="inline-flex items-center gap-1 text-xs text-secondary-700 bg-secondary-100 hover:bg-secondary-200 border border-secondary-200 px-2.5 py-1.5 rounded-md whitespace-nowrap cursor-pointer transition-colors"
                              >
                                <i className="ri-lock-unlock-line"></i>
                                {isDe ? "Freigeben" : "Unblock"}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        {tab === "touren" && (
          <TourBlockedDates pin={storedPin} isDe={isDe} />
        )}
        {tab === "fotos" && (
          <div className="max-w-4xl">
            <h2 className="font-heading text-2xl text-foreground-950 mb-3">
              {isDe ? "Fotos hochladen" : "Upload photos"}
            </h2>
            <p className="text-sm text-foreground-600 mb-6">
              {isDe
                ? "Wähle einen Zielordner und lade Fotos per Klick oder Drag & Drop hoch. Ein Bild austauschen = eine neue Datei mit demselben Namen hochladen."
                : "Choose a target folder and upload photos by click or drag & drop. To replace an image, upload a new file with the same name."}
            </p>

            <div className="mb-6">
              <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                {isDe ? "Zielordner" : "Target folder"}
              </label>
              <select
                value={uploadFolder}
                onChange={(e) => setUploadFolder(e.target.value)}
                className="w-full sm:max-w-xs px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
              >
                {PHOTO_FOLDERS.map((f) => (
                  <option key={f.value} value={f.value}>
                    {isDe ? f.labelDe : f.labelEn}
                  </option>
                ))}
              </select>
            </div>

            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                handleFilesSelected(e.dataTransfer.files);
              }}
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-background-300 rounded-lg p-10 text-center cursor-pointer hover:border-primary-400 transition-colors bg-background-50"
            >
              <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-700 mb-3">
                <i className="ri-image-add-line text-3xl"></i>
              </div>
              <p className="text-sm text-foreground-800 font-medium">
                {isDe
                  ? "Fotos hierher ziehen oder klicken zum Auswählen"
                  : "Drag photos here or click to select"}
              </p>
              <p className="text-xs text-foreground-500 mt-1">
                {isDe
                  ? "JPG, PNG, WebP — mehrere gleichzeitig möglich"
                  : "JPG, PNG, WebP — multiple at once"}
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => {
                  if (e.target.files) handleFilesSelected(e.target.files);
                  e.target.value = "";
                }}
              />
            </div>

            {selectedFiles.length > 0 && (
              <div className="mt-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-foreground-800">
                    {isDe
                      ? `${selectedFiles.length} Foto(s) ausgewählt`
                      : `${selectedFiles.length} photo(s) selected`}
                  </span>
                  <button
                    onClick={clearSelectedFiles}
                    className="text-xs text-foreground-600 hover:text-foreground-950 cursor-pointer whitespace-nowrap"
                  >
                    {isDe ? "Alle entfernen" : "Remove all"}
                  </button>
                </div>
                <div className="flex flex-wrap gap-3">
                  {selectedFiles.map((file, index) => (
                    <div key={`${file.name}-${index}`} className="relative group">
                      <div className="w-24 h-24 rounded-md overflow-hidden bg-background-100 border border-background-200">
                        <img
                          src={URL.createObjectURL(file)}
                          alt={file.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <button
                        onClick={() => removeSelectedFile(index)}
                        className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center rounded-full bg-secondary-500 text-background-50 cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity"
                        aria-label={isDe ? "Entfernen" : "Remove"}
                      >
                        <i className="ri-close-line text-sm"></i>
                      </button>
                      <p className="mt-1 text-[11px] text-foreground-500 truncate w-24" title={file.name}>
                        {file.name}
                      </p>
                    </div>
                  ))}
                </div>
                <button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="mt-5 inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-600 disabled:opacity-60 text-background-50 text-sm font-medium px-6 py-2.5 rounded-md whitespace-nowrap cursor-pointer transition-colors"
                >
                  {uploading && <i className="ri-loader-4-line animate-spin"></i>}
                  <i className="ri-upload-cloud-2-line"></i>
                  {isDe ? "Hochladen" : "Upload"}
                </button>
              </div>
            )}

            {uploadResult && (
              <div className="mt-5 text-sm font-medium bg-background-100 border border-background-200 rounded-md px-4 py-3 text-foreground-800">
                {uploadResult}
              </div>
            )}

            <div className="mt-10">
              <h3 className="font-heading text-lg text-foreground-950 mb-1">
                {isDe ? "Vorhandene Bilder im Ordner" : "Existing images in folder"}
              </h3>
              <p className="text-xs text-foreground-500 mb-4">{uploadFolder}/</p>
              {listLoading ? (
                <div className="flex items-center gap-2 text-sm text-foreground-600">
                  <i className="ri-loader-4-line animate-spin"></i>
                  {isDe ? "Wird geladen..." : "Loading..."}
                </div>
              ) : existingImages.length === 0 ? (
                <div className="bg-background-100 border border-background-200 rounded-lg p-8 text-center text-sm text-foreground-600">
                  {isDe ? "Noch keine Bilder in diesem Ordner." : "No images in this folder yet."}
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {existingImages.map((imgItem) => (
                    <div key={imgItem.path} className="relative group bg-background-50 border border-background-200 rounded-md p-2">
                      <div className="w-full h-20 rounded overflow-hidden bg-background-100">
                        <img
                          src={`${IMG_BASE}/${imgItem.path}`}
                          alt={imgItem.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <button
                        onClick={() => handleDeleteImage(imgItem.path, imgItem.name)}
                        className="absolute top-0 right-0 w-6 h-6 flex items-center justify-center rounded-full bg-secondary-500 text-background-50 cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity"
                        aria-label={isDe ? "Bild löschen" : "Delete image"}
                        title={isDe ? "Bild löschen" : "Delete image"}
                      >
                        <i className="ri-delete-bin-line text-sm"></i>
                      </button>
                      <p className="mt-1.5 text-[11px] text-foreground-500 truncate" title={imgItem.name}>
                        {imgItem.name}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>
        )}
        {tab === "transfers" && (
          <TransferPrices pin={storedPin} isDe={isDe} />
        )}
        {tab === "captions" && (
          <div className="max-w-4xl">
            <div className="flex items-start justify-between gap-4 mb-3">
              <div>
                <h2 className="font-heading text-2xl text-foreground-950">
                  {isDe ? "Bild-Beschriftungen" : "Image captions"}
                </h2>
                <p className="text-sm text-foreground-600 mt-1">
                  {isDe
                    ? "Wähle eine Galerie und passe die Texte unter den Bildern an. Änderungen sind sofort auf der Website sichtbar."
                    : "Choose a gallery and adjust the texts below the images. Changes appear on the website immediately."}
                </p>
              </div>
              <button
                onClick={handleSaveCaptions}
                disabled={captionSaving}
                className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-60 text-background-50 text-sm font-medium px-6 py-2.5 rounded-md whitespace-nowrap cursor-pointer transition-colors"
              >
                {captionSaving && <i className="ri-loader-4-line animate-spin"></i>}
                <i className="ri-save-line"></i>
                {isDe ? "Speichern" : "Save"}
              </button>
            </div>

            <div className="mb-3 flex flex-col sm:flex-row sm:items-end gap-4">
              <div className="w-full sm:max-w-sm">
                <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                  {isDe ? "Galerie" : "Gallery"}
                </label>
                <select
                  value={captionGallery}
                  onChange={(e) => setCaptionGallery(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
                >
                  {GALLERY_REGISTRY.map((g) => (
                    <option key={g.key} value={g.key}>
                      {isDe ? g.labelDe : g.labelEn}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                  {isDe ? "Sprache" : "Language"}
                </label>
                <div className="inline-flex items-center rounded-full bg-background-100 border border-background-200 p-1">
                  <button
                    type="button"
                    onClick={() => setCaptionLang("de")}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                      captionLang === "de"
                        ? "bg-accent-500 text-background-50"
                        : "text-foreground-700 hover:text-foreground-950"
                    }`}
                  >
                    Deutsch
                  </button>
                  <button
                    type="button"
                    onClick={() => setCaptionLang("en")}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                      captionLang === "en"
                        ? "bg-accent-500 text-background-50"
                        : "text-foreground-700 hover:text-foreground-950"
                    }`}
                  >
                    English
                  </button>
                </div>
              </div>
            </div>

            <p className="text-xs text-foreground-500 mb-6">
              {isDe
                ? "Deutsch und Englisch werden getrennt gepflegt. Die englischen Texte erscheinen auf den /en-Seiten."
                : "German and English are maintained separately. The English texts appear on the /en pages."}
            </p>

            {captionLoading ? (
              <div className="flex items-center gap-2 text-sm text-foreground-600 py-8">
                <i className="ri-loader-4-line animate-spin"></i>
                {isDe ? "Wird geladen..." : "Loading..."}
              </div>
            ) : !captionDef ? (
              <div className="bg-background-100 border border-background-200 rounded-lg p-8 text-center text-sm text-foreground-600">
                {isDe ? "Keine Galerie ausgewählt." : "No gallery selected."}
              </div>
            ) : (
              <div className="space-y-3">
                {captionDef.images.map((image) => {
                  const hasOverride =
                    captionOverrides[overrideKey(captionScopedKey, image.key)] !== undefined;
                  const draftVal = captionDraft[image.key] ?? "";
                  return (
                    <div
                      key={image.key}
                      className="flex items-center gap-4 bg-background-50 border border-background-200 rounded-lg p-3"
                    >
                      <div className="w-20 h-16 rounded-md overflow-hidden bg-background-100 flex-shrink-0 border border-background-200">
                        <img
                          src={`${IMG_BASE}/${image.key}`}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1.5">
                          <span className="text-[11px] text-foreground-500 truncate">
                            {image.key}
                          </span>
                          {hasOverride && (
                            <span className="text-[10px] bg-accent-100 text-accent-800 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                              {isDe ? "angepasst" : "custom"}
                            </span>
                          )}
                        </div>
                        <input
                          type="text"
                          value={draftVal}
                          onChange={(e) =>
                            setCaptionDraft((prev) => ({
                              ...prev,
                              [image.key]: e.target.value,
                            }))
                          }
                          placeholder={defaultForLang(image, captionLang)}
                          maxLength={200}
                          className="w-full px-3 py-2 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
                        />
                        <div className="mt-1 text-[11px] text-foreground-500 truncate">
                          {isDe ? "Standard" : "Default"}:{" "}
                          {defaultForLang(image, captionLang)}
                        </div>
                      </div>
                      <button
                        onClick={() => resetCaptionRow(image.key)}
                        title={isDe ? "Auf Standard zurücksetzen" : "Reset to default"}
                        className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-md text-foreground-600 hover:text-foreground-950 hover:bg-background-100 cursor-pointer transition-colors"
                      >
                        <i className="ri-arrow-go-back-line text-lg"></i>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
        {tab === "preise" && (
          <div className="max-w-3xl">
            <div className="flex items-start justify-between gap-4 mb-3">
              <div>
                <h2 className="font-heading text-2xl text-foreground-950">
                  {isDe ? "Baustein-Preise" : "Module prices"}
                </h2>
                <p className="text-sm text-foreground-600 mt-1">
                  {isDe
                    ? "Diese Preise werden für die Baustein-Berechnung auf der Webseite verwendet. Änderungen sind sofort aktiv."
                    : "These prices are used for the module calculation on the website. Changes take effect immediately."}
                </p>
                <p className="text-xs text-foreground-500 mt-1">
                  {isDe
                    ? `Alle US-Dollar-Preise werden mit Ihrem Kurs umgerechnet (1 € = ${exchangeRate} US$).`
                    : `All US dollar prices are calculated with your rate (1 € = ${exchangeRate} US$).`}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 shrink-0">
                <button
                  onClick={handlePrintPrices}
                  disabled={priceLoading || prices.length === 0}
                  className="inline-flex items-center justify-center gap-2 bg-background-100 hover:bg-background-200 disabled:opacity-60 text-foreground-800 border border-background-300 text-sm font-medium px-5 py-2.5 rounded-md whitespace-nowrap cursor-pointer transition-colors"
                >
                  <i className="ri-file-pdf-2-line"></i>
                  {isDe ? "Als PDF drucken" : "Print as PDF"}
                </button>
                <button
                  onClick={handleSavePrices}
                  disabled={priceSaving}
                  className="inline-flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-60 text-background-50 text-sm font-medium px-6 py-2.5 rounded-md whitespace-nowrap cursor-pointer transition-colors"
                >
                  {priceSaving && <i className="ri-loader-4-line animate-spin"></i>}
                  <i className="ri-save-line"></i>
                  {isDe ? "Speichern" : "Save"}
                </button>
              </div>
            </div>

            <div className="bg-background-50 border border-background-200 rounded-lg p-5 mb-5">
              <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                <div className="flex-1">
                  <h3 className="font-heading text-base text-foreground-950 mb-1">
                    {isDe ? "Umrechnungskurs" : "Exchange rate"}
                  </h3>
                  <p className="text-xs text-foreground-500">
                    {isDe
                      ? "Geben Sie den Kurs selbst ein (1 € → US$). Alle US-Dollar-Preise werden automatisch daraus berechnet."
                      : "Enter the rate yourself (1 € → US$). All US dollar prices are calculated from it automatically."}
                  </p>
                </div>
                <div className="shrink-0">
                  <span className="block text-[10px] uppercase tracking-wide text-foreground-500 mb-1 text-center">
                    1 € =
                  </span>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={0.01}
                      step="0.01"
                      value={exchangeRate}
                      onChange={(e) => setExchangeRate(Number(e.target.value))}
                      className="w-28 px-3 py-2 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 text-right focus:outline-none focus:ring-2 focus:ring-primary-400"
                    />
                    <span className="text-sm font-medium text-foreground-800">US$</span>
                  </div>
                </div>
              </div>
            </div>

            {priceLoading ? (
              <div className="flex items-center gap-2 text-sm text-foreground-600 py-8">
                <i className="ri-loader-4-line animate-spin"></i>
                {isDe ? "Wird geladen..." : "Loading..."}
              </div>
            ) : (
              <div className="space-y-5">
                {PRICE_GROUPS.map((group) => {
                  const items = prices.filter((p) => p.category === group.category);
                  if (items.length === 0) return null;
                  return (
                    <div key={group.category} className="bg-background-50 border border-background-200 rounded-lg p-5">
                      <h3 className="font-heading text-base text-foreground-950 mb-4">
                        {isDe ? group.labelDe : group.labelEn}
                      </h3>
                      <div className="space-y-3">
                        {items.map((p) => {
                          const isCurrency =
                            p.unit !== "percent" && p.unit !== "nights" && p.unit !== "excursions" && p.unit !== "rate";
                          const usdValue = toUsd(p.amount, exchangeRate);
                          return (
                            <div
                              key={p.key}
                              className="flex flex-col sm:flex-row sm:items-end justify-between gap-3"
                            >
                              <div className="flex-1 min-w-0">
                                <span className="block text-sm text-foreground-800">
                                  {isDe ? p.label_de : p.label_en}
                                </span>
                                <span className="block text-xs text-foreground-500 mt-0.5">
                                  {unitLabel(p.unit)}
                                </span>
                              </div>
                              {isCurrency ? (
                                <div className="flex items-start gap-3 shrink-0">
                                  <div>
                                    <span className="block text-[10px] uppercase tracking-wide text-foreground-500 mb-1 text-center">
                                      €
                                    </span>
                                    <input
                                      type="number"
                                      min={0}
                                      step="1"
                                      value={p.amount}
                                      onChange={(e) =>
                                        updatePrice(p.key, "amount", e.target.value)
                                      }
                                      className="w-24 px-3 py-2 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 text-right focus:outline-none focus:ring-2 focus:ring-primary-400"
                                    />
                                  </div>
                                  <div>
                                    <span className="block text-[10px] uppercase tracking-wide text-foreground-500 mb-1 text-center">
                                      US$
                                    </span>
                                    <div className="w-24 px-3 py-2 rounded-md border border-background-200 bg-background-100 text-sm text-foreground-700 text-right">
                                      {formatAmount(usdValue, false)}
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <input
                                  type="number"
                                  min={0}
                                  step="1"
                                  value={p.amount}
                                  onChange={(e) =>
                                    updatePrice(p.key, "amount", e.target.value)
                                  }
                                  className="w-24 px-3 py-2 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 text-right focus:outline-none focus:ring-2 focus:ring-primary-400 shrink-0"
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </main>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={closeEdit}
          ></div>
          <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-background-50 border border-background-200 rounded-lg">
            <div className="flex items-center justify-between px-6 py-4 border-b border-background-200 sticky top-0 bg-background-50">
              <h3 className="font-heading text-lg text-foreground-950">
                {isDe ? "Reservierung bearbeiten" : "Edit reservation"}
              </h3>
              <button
                onClick={closeEdit}
                className="w-9 h-9 flex items-center justify-center rounded-md bg-red-600 text-white hover:bg-red-700 cursor-pointer transition-colors"
                aria-label={isDe ? "Schließen" : "Close"}
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <form onSubmit={handleSaveEdit} className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                  {isDe ? "Villa" : "Villa"}
                </label>
                <select
                  value={editing.villa_slug}
                  onChange={(e) => updateEditField("villa_slug", e.target.value)}
                  className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
                >
                  {VILLAS.map((v) => (
                    <option key={v.slug} value={v.slug}>
                      {v.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                    {isDe ? "Anreise" : "Check-in"}
                  </label>
                  <DatePicker
                    value={editing.check_in}
                    onChange={(v) => updateEditField("check_in", v)}
                    isDe={isDe}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                    {isDe ? "Abreise" : "Check-out"}
                  </label>
                  <DatePicker
                    value={editing.check_out}
                    onChange={(v) => updateEditField("check_out", v)}
                    min={editing.check_in || undefined}
                    isDe={isDe}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                    {isDe ? "Erwachsene" : "Adults"}
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={editing.adults}
                    onChange={(e) => updateEditField("adults", Number(e.target.value))}
                    className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                    {isDe ? "Kinder" : "Children"}
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={editing.children}
                    onChange={(e) => updateEditField("children", Number(e.target.value))}
                    className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                  {isDe ? "Vorname" : "First name"}
                </label>
                <input
                  type="text"
                  value={editing.firstname}
                  onChange={(e) => updateEditField("firstname", e.target.value)}
                  className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                  {isDe ? "Nachname" : "Last name"}
                </label>
                <input
                  type="text"
                  value={editing.lastname}
                  onChange={(e) => updateEditField("lastname", e.target.value)}
                  className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                  {isDe ? "E-Mail" : "Email"}
                </label>
                <input
                  type="email"
                  value={editing.email}
                  onChange={(e) => updateEditField("email", e.target.value)}
                  className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                  {isDe ? "Telefon" : "Phone"}
                </label>
                <input
                  type="text"
                  value={editing.phone ?? ""}
                  onChange={(e) => updateEditField("phone", e.target.value)}
                  className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                  {isDe ? "Nationalität" : "Nationality"}
                </label>
                <input
                  type="text"
                  value={editing.nationality ?? ""}
                  onChange={(e) => updateEditField("nationality", e.target.value)}
                  className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                  {isDe ? "Nachricht" : "Message"}
                </label>
                <textarea
                  rows={3}
                  value={editing.message ?? ""}
                  onChange={(e) => updateEditField("message", e.target.value)}
                  className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-y"
                />
              </div>

              <div className="sm:col-span-2 flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeEdit}
                  className="inline-flex items-center gap-2 bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium px-6 py-2.5 rounded-md whitespace-nowrap cursor-pointer transition-colors"
                >
                  {isDe ? "Abbrechen" : "Cancel"}
                </button>
                <button
                  type="submit"
                  disabled={editSaving}
                  className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-60 text-background-50 text-sm font-medium px-6 py-2.5 rounded-md whitespace-nowrap cursor-pointer transition-colors"
                >
                  {editSaving && <i className="ri-loader-4-line animate-spin"></i>}
                  {isDe ? "Speichern" : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}